import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "./api";
import {
  DeployListSchema,
  DomainListSchema,
  PlansResponseSchema,
  SiteListSchema,
  SiteSchema,
  SubscriptionSchema,
  WorkspaceSchema,
} from "./schemas";

export interface SiteListSearch {
  query?: string;
  limit?: number;
  cursor?: string;
}

export const siteQueries = {
  all: ["sites"] as const,
  list: (search?: SiteListSearch) =>
    queryOptions({
      queryKey: [...siteQueries.all, "list", search ?? {}] as const,
      queryFn: ({ signal }) =>
        apiFetch("/sites/search", SiteListSchema, {
          method: "POST",
          body: JSON.stringify({
            limit: search?.limit ?? 100,
            query: search?.query,
            cursor: search?.cursor,
          }),
          signal,
        }),
    }),
  // Note: the path param is `site_slug` on the API; we pass the site slug here.
  detail: (siteSlug: string) =>
    queryOptions({
      queryKey: [...siteQueries.all, "detail", siteSlug] as const,
      queryFn: ({ signal }) => apiFetch(`/sites/${siteSlug}`, SiteSchema, { signal }),
    }),
};

export const deployQueries = {
  all: ["deploys"] as const,
  forSite: (siteSlug: string, limit = 20) =>
    queryOptions({
      queryKey: [...deployQueries.all, siteSlug, limit] as const,
      queryFn: ({ signal }) =>
        apiFetch(`/sites/${siteSlug}/deploys/search`, DeployListSchema, {
          method: "POST",
          body: JSON.stringify({ limit }),
          signal,
        }),
    }),
};

export const domainQueries = {
  all: ["domains"] as const,
  forSite: (siteSlug: string) =>
    queryOptions({
      queryKey: [...domainQueries.all, siteSlug] as const,
      queryFn: ({ signal }) => apiFetch(`/sites/${siteSlug}/domains`, DomainListSchema, { signal }),
    }),
};

export const billingQueries = {
  all: ["billing"] as const,
  subscription: () =>
    queryOptions({
      queryKey: [...billingQueries.all, "subscription"] as const,
      queryFn: ({ signal }) => apiFetch("/billing/subscription", SubscriptionSchema, { signal }),
      staleTime: 30_000,
    }),
  plans: () =>
    queryOptions({
      queryKey: [...billingQueries.all, "plans"] as const,
      queryFn: ({ signal }) => apiFetch("/billing/plans", PlansResponseSchema, { signal }),
      staleTime: 5 * 60_000,
    }),
};

// /workspaces/me never 404s — the handler auto-creates on miss (D11
// fallback). The earlier 404-as-null wrapper has been removed; callers can
// rely on the workspace always being present once the user is authed.
export const workspaceQueries = {
  all: ["workspaces"] as const,
  me: () =>
    queryOptions({
      queryKey: [...workspaceQueries.all, "me"] as const,
      queryFn: ({ signal }) => apiFetch("/workspaces/me", WorkspaceSchema, { signal }),
      staleTime: 60_000,
    }),
};
